import axios from 'axios';

// Detectar si estamos en un subdominio de Kontrol
const hostname = window.location.hostname;
const isKontrolTenant = hostname.endsWith('.kontrol.farmavida.site');
const BACKEND_URL = isKontrolTenant 
  ? `https://${hostname}`
  : (process.env.REACT_APP_BACKEND_URL || 'https://farmavida.site');
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add authorization header if token exists
    const token = localStorage.getItem('farmavida_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle token expiration/invalid token
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('farmavida_token');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login') && !window.location.pathname === '/') {
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

// API functions

// Dashboard & Metrics
export const getDashboardMetrics = async (startDate, endDate, storeId = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  
  const response = await apiClient.get('/dashboard/metrics', { params });
  return response.data;
};

// Stores API
export const getStores = async () => {
  const response = await apiClient.get('/stores');
  return response.data;
};

export const getStore = async (storeId) => {
  const response = await apiClient.get(`/stores/${storeId}`);
  return response.data;
};

export const createStore = async (storeData) => {
  const response = await apiClient.post('/stores', storeData);
  return response.data;
};

export const updateStore = async (storeId, storeData) => {
  const response = await apiClient.put(`/stores/${storeId}`, storeData);
  return response.data;
};

export const deleteStore = async (storeId) => {
  const response = await apiClient.delete(`/stores/${storeId}`);
  return response.data;
};

// Sales API
export const getSales = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  
  const response = await apiClient.get('/sales', { params });
  return response.data;
};

export const getSale = async (saleId) => {
  const response = await apiClient.get(`/sales/${saleId}`);
  return response.data;
};

export const createSale = async (saleData) => {
  const response = await apiClient.post('/sales', saleData);
  return response.data;
};

export const updateSale = async (saleId, saleData) => {
  const response = await apiClient.put(`/sales/${saleId}`, saleData);
  return response.data;
};

export const deleteSale = async (saleId) => {
  const response = await apiClient.delete(`/sales/${saleId}`);
  return response.data;
};

// Expenses API
export const getExpenses = async (filters = {}) => {
  const params = {};
  if (filters.category) params.category = filters.category;
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.reference) params.reference = filters.reference;
  if (filters.sortField) params.sort_field = filters.sortField;
  if (filters.sortDir) params.sort_dir = filters.sortDir;

  const response = await apiClient.get('/expenses', { params });
  return response.data;
};

export const getExpense = async (expenseId) => {
  const response = await apiClient.get(`/expenses/${expenseId}`);
  return response.data;
};

export const createExpense = async (expenseData) => {
  const response = await apiClient.post('/expenses', expenseData);
  return response.data;
};

export const deleteExpense = async (expenseId) => {
  const response = await apiClient.delete(`/expenses/${expenseId}`);
  return response.data;
};

// Inventory API
export const getInventoryPurchases = async (filters = {}) => {
  const params = { limit: filters.limit ?? 200 };
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.invoiceNumber) params.invoice_number = filters.invoiceNumber;

  const response = await apiClient.get('/inventory', { params });
  return response.data;
};

export const getInventoryPurchasesSummary = async () => {
  const response = await apiClient.get('/inventory/summary');
  return response.data;
};

export const getInventoryPurchase = async (purchaseId) => {
  const response = await apiClient.get(`/inventory/${purchaseId}`);
  return response.data;
};

export const createInventoryPurchase = async (purchaseData) => {
  const response = await apiClient.post('/inventory', purchaseData);
  return response.data;
};

export const updateInventoryPurchase = async (purchaseId, purchaseData) => {
  const response = await apiClient.put(`/inventory/${purchaseId}`, purchaseData);
  return response.data;
};

export const deleteInventoryPurchase = async (purchaseId) => {
  const response = await apiClient.delete(`/inventory/${purchaseId}`);
  return response.data;
};

// Supplier Management API
// GET /suppliers → [{id, name, aliases, credit_days, discount_pct}]
export const getSuppliers = async () => {
  const response = await apiClient.get('/suppliers');
  return response.data;
};

// POST /suppliers → crea un proveedor canónico nuevo
export const createSupplier = async (data) => {
  const response = await apiClient.post('/suppliers', data);
  return response.data;
};

// PUT /suppliers/{id} → actualiza nombre, aliases, etc.
export const updateSupplier = async (id, data) => {
  const response = await apiClient.put(`/suppliers/${id}`, data);
  return response.data;
};

// DELETE /suppliers/{id} → desactiva (solo si sin facturas)
export const deleteSupplier = async (supplierId) => {
  const response = await apiClient.delete(`/suppliers/${supplierId}`);
  return response.data;
};

// POST /suppliers/merge → fusiona proveedores (por nombre)
export const mergeSuppliers = async (oldName, newName) => {
  const response = await apiClient.post('/suppliers/merge', null, {
    params: { old_name: oldName, new_name: newName }
  });
  return response.data;
};

// POST /suppliers/migrate → migración única: nombres de inventory_purchases → colección suppliers
export const migrateSuppliers = async () => {
  const response = await apiClient.post('/suppliers/migrate');
  return response.data;
};


// Accounts Payable API
export const getAccountsPayable = async (storeId = null, startDate = null, endDate = null) => {
  const params = {};
  if (storeId) params.store_id = storeId;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  // Increase timeout for this endpoint as it processes many records
  const response = await apiClient.get('/accounts-payable', { 
    params,
    timeout: 30000  // 30 seconds timeout
  });
  return response.data;
};

export const getAccountsPayableSummary = async () => {
  const response = await apiClient.get('/accounts-payable/summary');
  return response.data;
};

export const createPaymentRecord = async (paymentData) => {
  const response = await apiClient.post('/payments', paymentData);
  return response.data;
};

export const createBulkPayment = async (bulkData) => {
  const response = await apiClient.post('/payments/bulk', bulkData);
  return response.data;
};

export const getPaymentsForPurchase = async (purchaseId) => {
  const response = await apiClient.get(`/payments/${purchaseId}`);
  return response.data;
};

export const deletePaymentRecord = async (paymentId) => {
  const response = await apiClient.delete(`/payments/${paymentId}`);
  return response.data;
};

export const uploadPaymentDocument = async (paymentId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post(`/upload-payment-document/${paymentId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const downloadPaymentDocument = async (paymentId) => {
  const response = await apiClient.get(`/download-payment-document/${paymentId}`, {
    responseType: 'blob',
  });
  return response.data;
};

export const deletePaymentDocument = async (paymentId) => {
  const response = await apiClient.delete(`/delete-payment-document/${paymentId}`);
  return response.data;
};

// Paid Invoices API
export const getPaidInvoices = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.supplierName) params.supplier_name = filters.supplierName;
  if (filters.paymentMethod) params.payment_method = filters.paymentMethod;
  if (filters.limit) params.limit = filters.limit;
  
  const response = await apiClient.get('/paid-invoices', { params });
  return response.data;
};

// Audit API
export const getPaymentsAudit = async (params = {}) => {
  const response = await apiClient.get('/audit/daily-payments', { params });
  return response.data;
};

export const reviewPaymentAudit = async (paymentId, action, notes = '') => {
  const response = await apiClient.post(`/audit/payments/${paymentId}/review`, { action, notes });
  return response.data;
};

export const getPaymentAuditSummary = async (days = 30) => {
  const response = await apiClient.get('/audit/summary', { params: { days } });
  return response.data;
};

// Varios comprobantes por pago / lote / gasto
const _multi = (files) => { const fd = new FormData(); (Array.isArray(files) ? files : [files]).filter(Boolean).forEach(f => fd.append('files', f)); return fd; };
export const uploadPaymentDocuments = async (paymentId, files) =>
  (await apiClient.post(`/payments/${paymentId}/documents`, _multi(files), { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data;
export const uploadBatchPaymentDocuments = async (batchId, files) =>
  (await apiClient.post(`/payments/batch-document/${batchId}`, _multi(files), { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data;
export const deletePaymentDocumentFile = async (paymentId, fileId) => (await apiClient.delete(`/payments/${paymentId}/documents/${fileId}`)).data;
export const uploadExpenseDocuments = async (expenseId, files) =>
  (await apiClient.post(`/expenses/${expenseId}/documents`, _multi(files), { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data;
export const deleteExpenseDocumentFile = async (expenseId, fileId) => (await apiClient.delete(`/expenses/${expenseId}/documents/${fileId}`)).data;
// Abre un PDF protegido en una pestaña nueva (descarga con token y crea un blob)
export const openProtectedPdf = async (path) => {
  const res = await apiClient.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
export const openPaymentDocumentFile = (paymentId, fileId) => openProtectedPdf(`/payments/${paymentId}/documents/${fileId}`);
export const openTransferDocumentFile = (transferId, fileId) => openProtectedPdf(`/bank-transfers/${transferId}/documents/${fileId}`);
// Traspasos entre bancos
export const listBankTransfers = async (params = {}) => (await apiClient.get('/bank-transfers', { params })).data;
export const createBankTransfer = async (data) => (await apiClient.post('/bank-transfers', data)).data;
export const deleteBankTransfer = async (id) => (await apiClient.delete(`/bank-transfers/${id}`)).data;
export const uploadTransferDocuments = async (id, files) =>
  (await apiClient.post(`/bank-transfers/${id}/documents`, _multi(files), { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 })).data;
export const deleteTransferDocumentFile = async (id, fileId) => (await apiClient.delete(`/bank-transfers/${id}/documents/${fileId}`)).data;
export const openExpenseDocumentFile = (expenseId, fileId) => openProtectedPdf(`/expenses/${expenseId}/documents/${fileId}`);

export const uploadBatchPaymentDocument = async (batchId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/payments/batch-document/${batchId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getDailyPaymentsAudit = async (date, storeId) => {
  const params = {};
  if (date) params.date = date;
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/audit/daily-payments', { params });
  return response.data;
};

// Exchange Rate API
export const getExchangeRates = async (limit = 30) => {
  const params = { limit };
  const response = await apiClient.get('/exchange-rates', { params });
  return response.data;
};

export const getLatestExchangeRate = async () => {
  const response = await apiClient.get('/exchange-rates/latest');
  return response.data;
};

export const createExchangeRate = async (rateData) => {
  const response = await apiClient.post('/exchange-rates', rateData);
  return response.data;
};

export const deleteExchangeRate = async (rateId) => {
  const response = await apiClient.delete(`/exchange-rates/${rateId}`);
  return response.data;
};

// Reports API
export const getSalesByStore = async (startDate, endDate, storeId = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  
  const response = await apiClient.get('/reports/sales-by-store', { params });
  return response.data;
};

export const getDailyExpensesSummary = async (date, storeId = null) => {
  const params = { date };
  if (storeId) params.store_id = storeId;

  const response = await apiClient.get('/reports/daily-expenses-summary', { params });
  return response.data;
};

export const getExpensesByCategory = async (startDate, endDate, storeId = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  
  const response = await apiClient.get('/reports/expenses-by-category', { params });
  return response.data;
};

export const getProfitLossReport = async (startDate, endDate, storeId = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  
  const response = await apiClient.get('/reports/profit-loss', { params });
  return response.data;
};

export const getStoreProfitability = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/reports/store-profitability', { params });
  return response.data;
};

export const getPaymentMethodsReport = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.methods && filters.methods.length > 0) {
    params.methods = filters.methods.join(',');
  }
  
  const response = await apiClient.get('/reports/payment-methods', { params });
  return response.data;
};

export const getSupplierPurchasesReport = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.supplierName) params.supplier_name = filters.supplierName;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  
  const response = await apiClient.get('/reports/supplier-purchases', { params });
  return response.data;
};

export const updatePurchase = async (purchaseId, purchaseData) => {
  const response = await apiClient.put(`/inventory/${purchaseId}`, purchaseData);
  return response.data;
};
export const getUsers = async () => {
  const response = await apiClient.get('/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await apiClient.put(`/users/${userId}`, userData);
  return response.data;
};

export const updateUserStatus = async (userId, isActive) => {
  const response = await apiClient.patch(`/users/${userId}`, {
    is_active: isActive
  });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/users/${userId}`);
  return response.data;
};

// Break-Even Analysis API
export const createBreakEvenData = async (breakEvenData) => {
  const response = await apiClient.post('/break-even', breakEvenData);
  return response.data;
};

export const getBreakEvenData = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.periodMonth) params.period_month = filters.periodMonth;
  if (filters.periodYear) params.period_year = filters.periodYear;
  
  const response = await apiClient.get('/break-even', { params });
  return response.data;
};

export const updateBreakEvenData = async (breakEvenId, breakEvenData) => {
  const response = await apiClient.put(`/break-even/${breakEvenId}`, breakEvenData);
  return response.data;
};

export const deleteBreakEvenData = async (breakEvenId) => {
  const response = await apiClient.delete(`/break-even/${breakEvenId}`);
  return response.data;
};

export const getBreakEvenAnalysis = async (storeId, periodMonth, periodYear) => {
  const params = { period_month: periodMonth, period_year: periodYear };
  const response = await apiClient.get(`/break-even/${storeId}/analysis`, { params });
  return response.data;
};

export const getAutoBreakEvenAnalysis = async (storeId, periodMonth, periodYear) => {
  const params = { period_month: periodMonth, period_year: periodYear };
  const response = await apiClient.get(`/break-even/${storeId}/auto-analysis`, { params });
  return response.data;
};

// Initial Inventory Value API
export const createInitialInventoryValue = async (inventoryData) => {
  const response = await apiClient.post('/initial-inventory', inventoryData);
  return response.data;
};

export const getInitialInventoryValues = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.periodMonth) params.period_month = filters.periodMonth;
  if (filters.periodYear) params.period_year = filters.periodYear;
  
  const response = await apiClient.get('/initial-inventory', { params });
  return response.data;
};

export const updateInitialInventoryValue = async (inventoryId, inventoryData) => {
  const response = await apiClient.put(`/initial-inventory/${inventoryId}`, inventoryData);
  return response.data;
};

export const deleteInitialInventoryValue = async (inventoryId) => {
  const response = await apiClient.delete(`/initial-inventory/${inventoryId}`);
  return response.data;
};

export const getStoreFinancialBalance = async (storeId, periodMonth, periodYear) => {
  const params = { period_month: periodMonth, period_year: periodYear };
  const response = await apiClient.get(`/initial-inventory/${storeId}/balance`, { params });
  return response.data;
};

// WhatsApp Service API (through backend proxy)
export const getWhatsAppStatus = async () => {
  const response = await apiClient.get('/whatsapp/status');
  return response.data;
};

export const getWhatsAppQR = async () => {
  const response = await apiClient.get('/whatsapp/qr');
  return response.data;
};

export const restartWhatsAppService = async () => {
  const response = await apiClient.post('/whatsapp/restart');
  return response.data;
};

export const sendTestNotification = async () => {
  const response = await apiClient.post('/whatsapp/send-test-message');
  return response.data;
};

// Cash Control API
export const getCashBalance = async () => {
  const response = await apiClient.get('/cash-control/balance');
  return response.data;
};

export const getCashMovements = async (startDate, endDate, movementType) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (movementType) params.movement_type = movementType;
  
  const response = await apiClient.get('/cash-control/movements', { params });
  return response.data;
};

export const createCashAdjustment = async (adjustmentData) => {
  const response = await apiClient.post('/cash-control/adjustment', adjustmentData);
  return response.data;
};

export const createCashArqueo = async (arqueoData) => {
  const response = await apiClient.post('/cash-control/arqueo', arqueoData);
  return response.data;
};

export const createDepositoBanco = async (data) => {
  const response = await apiClient.post('/cash-control/deposito-banco', data);
  return response.data;
};

export const getDailyCashReport = async (date) => {
  const response = await apiClient.get('/cash-control/daily-report', {
    params: { date }
  });
  return response.data;
};

// Bank Config API
export const getTenantBanks = async () => {
  const response = await apiClient.get('/tenant-banks');
  return response.data;
};

export const updateTenantBanks = async (banks) => {
  const response = await apiClient.put('/tenant-banks', banks);
  return response.data;
};

export const addTenantBank = async (bank) => {
  const response = await apiClient.post('/tenant-banks/bank', bank);
  return response.data;
};

export const toggleTenantBank = async (key) => {
  const response = await apiClient.patch(`/tenant-banks/${key}/toggle`);
  return response.data;
};

export const deleteTenantBank = async (key) => {
  const response = await apiClient.delete(`/tenant-banks/${key}`);
  return response.data;
};

export const getUnitsSoldReport = async (startDate, endDate, storeId) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/reports/units-sold', { params });
  return response.data;
};

export const getInventoryValuation = async (date = null, storeId = null) => {
  const params = {};
  if (date) params.date = date;
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/reports/inventory-valuation', { params });
  return response.data;
};

// ── Payment Approval API ──────────────────────────────────────────────────────

export const getPaymentApprovalConfig = async () => {
  const response = await apiClient.get('/payment-approval/config');
  return response.data;
};

export const updatePaymentApprovalConfig = async (config) => {
  const response = await apiClient.put('/payment-approval/config', config);
  return response.data;
};

export const getPaymentApprovalSummary = async () => {
  const response = await apiClient.get('/payment-approval/summary');
  return response.data;
};

export const listPaymentProposals = async (params = {}) => {
  const response = await apiClient.get('/payment-approval/proposals', { params });
  return response.data;
};

export const getPaymentProposal = async (id) => {
  const response = await apiClient.get(`/payment-approval/proposals/${id}`);
  return response.data;
};

export const createPaymentProposal = async (proposal) => {
  const response = await apiClient.post('/payment-approval/proposals', proposal);
  return response.data;
};

export const reviewPaymentProposal = async (id, action, notes = '') => {
  const response = await apiClient.post(`/payment-approval/proposals/${id}/review`, { action, notes });
  return response.data;
};

export const executePaymentProposal = async (id, file, executionNotes = '') => {
  const formData = new FormData();
  formData.append('receipt', file);
  formData.append('execution_notes', executionNotes);
  const response = await apiClient.post(
    `/payment-approval/proposals/${id}/execute`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const cancelPaymentProposal = async (id, notes = '') => {
  const response = await apiClient.post(
    `/payment-approval/proposals/${id}/cancel`,
    null,
    { params: { notes } }
  );
  return response.data;
};

// Planificación de pagos del día
export const getPlanAvailabilitySnapshot = async () => (await apiClient.get('/payment-plans/availability-snapshot')).data;
export const listPaymentPlans = async (params = {}) => (await apiClient.get('/payment-plans', { params })).data;
export const getPaymentPlan = async (id) => (await apiClient.get(`/payment-plans/${id}`)).data;
export const createPaymentPlan = async (plan) => (await apiClient.post('/payment-plans', plan)).data;
export const updatePaymentPlan = async (id, plan) => (await apiClient.put(`/payment-plans/${id}`, plan)).data;
export const sendPaymentPlan = async (id) => (await apiClient.post(`/payment-plans/${id}/send`)).data;
export const deletePaymentPlan = async (id) => (await apiClient.delete(`/payment-plans/${id}`)).data;
export const setPlanItemStatus = async (planId, itemId, status, notes = '') =>
  (await apiClient.post(`/payment-plans/${planId}/items/${itemId}/status`, { status, notes })).data;
export const payPlanItem = async (planId, itemId, fields, files) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
  (Array.isArray(files) ? files : [files]).filter(Boolean).forEach(f => fd.append('receipts', f));
  return (await apiClient.post(`/payment-plans/${planId}/items/${itemId}/pay`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
  })).data;
};
export const getPlanItemReceiptUrl = (planId, itemId) => `${API_BASE}/payment-plans/${planId}/items/${itemId}/receipt`;

export const getPaymentReceiptUrl = (proposalId) =>
  `${API_BASE}/payment-approval/proposals/${proposalId}/receipt`;

// Bank Availability API
export const getBankAvailability = async () => {
  const response = await apiClient.get('/bank-reconciliation/availability');
  return response.data;
};

// POS Batch Closings — Comparison engine
export const getPosBatchesDaily = async (date, storeId = null) => {
  const params = { date };
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/pos-batches/daily', { params });
  return response.data;
};

export const getReceivableClients = async ({ internal = false, storeId = null } = {}) => {
  const params = { internal };
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/receivables/clients', { params });
  return response.data;
};

export const getReceivableClient = async (key) => {
  const response = await apiClient.get(`/receivables/clients/${encodeURIComponent(key)}`);
  return response.data;
};

export const updateReceivableClient = async (key, data) => {
  const response = await apiClient.put(`/receivables/clients/${encodeURIComponent(key)}`, data);
  return response.data;
};

export const mergeReceivableClient = async (key, intoKey) => {
  const response = await apiClient.post(`/receivables/clients/${encodeURIComponent(key)}/merge`, null, { params: { into: intoKey } });
  return response.data;
};

export const addReceivablePayment = async (key, data) => {
  const response = await apiClient.post(`/receivables/clients/${encodeURIComponent(key)}/payments`, data);
  return response.data;
};

export const deleteReceivablePayment = async (paymentId) => {
  const response = await apiClient.delete(`/receivables/payments/${paymentId}`);
  return response.data;
};

export const addReceivableAdjustment = async (key, data) => {
  const response = await apiClient.post(`/receivables/clients/${encodeURIComponent(key)}/adjustments`, data);
  return response.data;
};

export const deleteReceivableAdjustment = async (adjustmentId) => {
  const response = await apiClient.delete(`/receivables/adjustments/${adjustmentId}`);
  return response.data;
};

export const syncReceivables = async () => {
  const response = await apiClient.post('/receivables/sync');
  return response.data;
};

export const getCashFlowDaily = async ({ dateFrom, dateTo, storeId = null }) => {
  const params = { date_from: dateFrom, date_to: dateTo };
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/cash-flow/daily', { params });
  return response.data;
};

export const getPosComparison = async ({ dateFrom, dateTo, storeId = null, settlementDays = 2 }) => {
  const params = { date_from: dateFrom, date_to: dateTo, settlement_days: settlementDays };
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get('/pos-batches/comparison', { params });
  return response.data;
};

// Cashea v2 — facturas desde Tecnodroid + ciclos semanales (miércoles)
export const getCasheaSummary = async () => {
  const response = await apiClient.get('/cashea/summary');
  return response.data;
};

export const getCasheaCycles = async (storeId = null, accountGroup = null, limit = 52) => {
  const params = { limit };
  if (storeId) params.store_id = storeId;
  if (accountGroup) params.account_group = accountGroup;
  const response = await apiClient.get('/cashea/cycles', { params });
  return response.data;
};

export const getCasheaCycleDetail = async (paymentDate, accountGroup, storeId = null) => {
  const params = { account_group: accountGroup };
  if (storeId) params.store_id = storeId;
  const response = await apiClient.get(`/cashea/cycles/${paymentDate}`, { params });
  return response.data;
};

export const getCasheaInvoices = async (filters = {}) => {
  const params = {};
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.paymentDate) params.payment_date = filters.paymentDate;
  if (filters.accountGroup) params.account_group = filters.accountGroup;
  if (filters.q) params.q = filters.q;
  if (filters.includeAnnulled) params.include_annulled = true;
  const response = await apiClient.get('/cashea/invoices', { params });
  return response.data;
};

export const registerCasheaCyclePayment = async (paymentDate, data) => {
  const response = await apiClient.post(`/cashea/cycles/${paymentDate}/payments`, data);
  return response.data;
};

export const deleteCasheaCyclePayment = async (paymentId) => {
  const response = await apiClient.delete(`/cashea/payments/${paymentId}`);
  return response.data;
};

export const setCasheaCycleRate = async (paymentDate, rate) => {
  const response = await apiClient.put(`/cashea/cycles/${paymentDate}/rate`, { rate });
  return response.data;
};

export const clearCasheaCycleRate = async (paymentDate) => {
  const response = await apiClient.delete(`/cashea/cycles/${paymentDate}/rate`);
  return response.data;
};

export const getCasheaConfig = async () => {
  const response = await apiClient.get('/cashea/config');
  return response.data;
};

export const updateCasheaConfig = async (data) => {
  const response = await apiClient.put('/cashea/config', data);
  return response.data;
};

export const runCasheaSync = async (dateFrom = null, dateTo = null) => {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const response = await apiClient.post('/cashea/sync', null, { params });
  return response.data;
};

// Cashea legado (registros manuales del esquema anterior)
export const getLegacyCasheaSales = async (status = 'pending') => {
  const response = await apiClient.get('/cashea-sales', { params: { status } });
  return response.data;
};

export const markLegacyCasheaSalePaid = async (saleId, data) => {
  const response = await apiClient.put(`/cashea-sales/${saleId}`, data);
  return response.data;
};

export const deleteLegacyCasheaSale = async (saleId) => {
  const response = await apiClient.delete(`/cashea-sales/${saleId}`);
  return response.data;
};

export default apiClient;